import type { HtmlSerializerProps } from './html-serializer';
import { Logger } from './logger';

interface ParsedMetaLanguage {
  fallback?: unknown;
  title?: unknown;
  type?: unknown;
  value?: unknown;
  successValue?: unknown;
}

type MetaLanguageContent = HtmlSerializerProps['content'];

interface MetaLanguageSinglePageSchema extends ParsedMetaLanguage {
  type: 'custom';
  value: MetaLanguageContent[];
}

interface MetaLanguageMultiPageSchema extends ParsedMetaLanguage {
  type: 'custom-multi-page';
  value: MetaLanguageContent[][];
  fallback?: MetaLanguageContent[];
}

interface MetaLanguageCarouselSchema extends ParsedMetaLanguage {
  type: 'carousel';
  value: MetaLanguageContent[][];
}

export type MetaLanguage =
  | {
      type: 'custom';
      content: MetaLanguageContent[];
      successContent?: MetaLanguageContent[];
      title?: string;
    }
  | {
      type: 'custom-multi-page';
      content: MetaLanguageContent[][];
      fallback: MetaLanguageContent[];
      title?: string;
    }
  | {
      type: 'carousel';
      content: MetaLanguageContent[][];
      title?: string;
    };

const isContentArray = (value: unknown): value is MetaLanguageContent[] =>
  Array.isArray(value);

const isContentPages = (value: unknown): value is MetaLanguageContent[][] =>
  Array.isArray(value) && value.every(isContentArray);

const isParsedMetaLanguage = (value: unknown): value is ParsedMetaLanguage =>
  typeof value === 'object' && value !== null;

const isMetaLanguageSinglePage = (
  value: unknown
): value is MetaLanguageSinglePageSchema =>
  isParsedMetaLanguage(value) &&
  value.type === 'custom' &&
  isContentArray(value.value);

const isMetaLanguageMultiPage = (
  value: unknown
): value is MetaLanguageMultiPageSchema =>
  isParsedMetaLanguage(value) &&
  value.type === 'custom-multi-page' &&
  isContentPages(value.value) &&
  (value.fallback === undefined || isContentArray(value.fallback));

const isMetaLanguageCarousel = (
  value: unknown
): value is MetaLanguageCarouselSchema =>
  isParsedMetaLanguage(value) &&
  value.type === 'carousel' &&
  isContentPages(value.value);

const getTitle = (value: ParsedMetaLanguage): string | undefined =>
  typeof value.title === 'string' ? value.title : undefined;

export const parseMetaLanguageSchema = (
  schema: unknown
): MetaLanguage | undefined => {
  if (isMetaLanguageSinglePage(schema)) {
    return {
      type: schema.type,
      content: schema.value,
      successContent: isContentArray(schema.successValue)
        ? schema.successValue
        : undefined,
      title: getTitle(schema),
    };
  }

  if (isMetaLanguageMultiPage(schema)) {
    return {
      type: schema.type,
      content: schema.value,
      fallback: schema.fallback ?? [],
      title: getTitle(schema),
    };
  }

  if (isMetaLanguageCarousel(schema)) {
    return {
      type: schema.type,
      content: schema.value,
      title: getTitle(schema),
    };
  }

  return undefined;
};

export const parseMetaLanguage = (
  message?: string
): MetaLanguage | undefined => {
  if (!message) return undefined;

  const sanitizedMessage = message.replace(/(\r\n|\n|\r)/gm, '');
  const jsonString = sanitizedMessage.match(
    /<PrescreenJson>(.*)<\/PrescreenJson>/
  )?.[1];

  if (jsonString === undefined) return undefined;

  try {
    return parseMetaLanguageSchema(JSON.parse(jsonString));
  } catch (error) {
    Logger.log(error);
    return undefined;
  }
};
